import React from 'react';
import { Calendar, Shield, ArrowRight, Wrench, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Index = () => {
  const featuredServices = [
    {
      title: 'Mobile Rust Removal',
      description: 'Professional on-site rust removal service. We come to you with all the equipment needed.',
      icon: Wrench,
      features: ['Same-day availability', 'Professional equipment', 'All vehicle types'],
      price: 'Starting at $199'
    },
    {
      title: 'Emergency Response',
      description: '24/7 emergency rust response for critical equipment failures and urgent repairs.',
      icon: Zap,
      features: ['24/7 availability', 'Rapid response time', 'Critical equipment priority'],
      price: 'Call for pricing'
    },
    {
      title: 'Protective Coatings',
      description: 'Advanced anti-rust coatings with 10-15 year warranties for long-term protection.',
      icon: Shield,
      features: ['10-15 year warranty', 'Marine-grade protection', 'Premium materials'],
      price: 'Starting at $299'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center w-full bg-gray-900 dark:bg-black">
        
        <div className="relative z-10 px-6 lg:px-8 py-20 text-center text-white w-full max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            Rust Protection
            <span className="block text-orange-400">& Maintenance</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 animate-fade-in opacity-90">
            Professional rust removal, prevention, and long-term protection for automotive, marine, and industrial equipment.
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center animate-fade-in">
            <Link 
              to="/contact"
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
            >
              <Calendar className="h-5 w-5" />
              <span>Book Free Rust Check</span>
            </Link>
            <Link 
              to="/gallery"
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 inline-flex items-center justify-center"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services Section */}
      <section className="py-20 px-6 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
              Featured <span className="text-orange-600 dark:text-orange-400">Services</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our most popular rust protection and removal services, trusted by thousands of customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {featuredServices.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-800"
              >
                <service.icon className="h-16 w-16 mb-6 text-orange-600 dark:text-orange-400 mx-auto" />
                <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600 dark:text-gray-300">
                      <CheckCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-4">
                    {service.price}
                  </div>
                  <button className="w-full bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200">
                    Get Quote
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/services"
              className="inline-flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              <span>View All Services</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Services Overview */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
            Protect Your <span className="text-orange-600 dark:text-orange-400">Investment</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-12">
            From mobile rust removal to long-term protection plans, we keep your equipment running smoothly
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg">
              <Shield className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Mobile Service</h3>
              <p className="text-gray-600 dark:text-gray-300">We come to you with professional equipment for on-site rust removal and protection</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg">
              <Shield className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">15+ Years Experience</h3>
              <p className="text-gray-600 dark:text-gray-300">Trusted expertise in automotive, marine, and industrial rust protection</p>
            </div>
            <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg">
              <Shield className="h-12 w-12 text-orange-600 dark:text-orange-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Guaranteed Results</h3>
              <p className="text-gray-600 dark:text-gray-300">Premium coatings with 10-15 year warranties and maintenance plans</p>
            </div>
          </div>

          <Link 
            to="/services"
            className="inline-flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105"
          >
            <span>View All Services</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Website for Sale Section */}
      <section className="py-20 px-6 bg-gray-900 dark:bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-orange-600 to-orange-700 dark:from-orange-700 dark:to-orange-800 rounded-2xl p-8 md:p-12 shadow-2xl">
            <div className="text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                This Website is Available for Purchase
              </h2>
              <p className="text-lg md:text-xl mb-6 opacity-95 max-w-3xl mx-auto">
                Looking for a professional, ready-to-use website? This website and many others are available for purchase. 
                You can enhance it, customise it to suit your business, and even deploy it on your own domain name.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-2">Fully Customisable</h3>
                  <p className="text-sm opacity-90">
                    Modify content, colours, and features to match your brand and business needs perfectly.
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-2">Multiple Websites Available</h3>
                  <p className="text-sm opacity-90">
                    Browse our collection of professional websites designed and developed by{' '}
                    <a 
                      href="https://appsshowroom.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="underline hover:no-underline font-medium"
                    >
                      Apps Showroom
                    </a>
                    {' '}and find the perfect fit for your business.
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-2">Your Domain, Your Brand</h3>
                  <p className="text-sm opacity-90">
                    Deploy on any domain name you choose. Complete ownership and control of your website.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:aideveloper@matthewweb.com"
                  className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 inline-flex items-center justify-center"
                >
                  Contact Us to Purchase
                </a>
                <a
                  href="https://appsshowroom.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 inline-flex items-center justify-center"
                >
                  View More Websites
                </a>
              </div>
              <p className="text-sm mt-6 opacity-80">
                Website designed and developed by{' '}
                <a 
                  href="https://appsshowroom.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Matthew Innes @ Apps Showroom
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-orange-600 dark:bg-orange-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Protect Your Equipment?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get a free rust assessment and discover how we can extend the life of your valuable assets
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link 
              to="/contact"
              className="w-full sm:w-auto bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
            >
              <Calendar className="h-5 w-5" />
              <span>Schedule Free Assessment</span>
            </Link>
            <Link 
              to="/about"
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:scale-105 inline-flex items-center justify-center"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
